import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { listResumeBatches, uploadResumeBatch } from "../services/jobService";
import "./RecruiterJobs.css";

const MAX_FILES = 50;
const POLL_MS = 3000;

// Upload up to 50 resumes at a time; the backend processes them asynchronously
// (FastAPI BackgroundTasks) so this modal only has to submit the batch and
// then poll for progress — it never blocks on scoring 50 resumes inline.
export default function ResumeUploadModal({ open, jobId, onClose, onBatchStarted }) {
  const [batches, setBatches] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const loadBatches = useCallback(async () => {
    try {
      setBatches(await listResumeBatches(jobId));
    } catch (err) {
      setError(err.message || "Unable to load batch history");
    }
  }, [jobId]);

  useEffect(() => { if (open && jobId) loadBatches(); }, [open, jobId, loadBatches]);

  useEffect(() => {
    if (!open) return undefined;
    const hasActive = batches.some((b) => b.status === "processing");
    if (!hasActive) return undefined;
    const id = setInterval(loadBatches, POLL_MS);
    return () => clearInterval(id);
  }, [open, batches, loadBatches]);

  if (!open) return null;

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (files.length > MAX_FILES) {
      toast.error(`Upload at most ${MAX_FILES} resumes per batch.`);
      return;
    }
    setUploading(true); setError("");
    try {
      const result = await uploadResumeBatch(jobId, files);
      toast.success(`Batch started — scoring ${result.totalFiles} resumes.`);
      onBatchStarted?.();
      await loadBatches();
    } catch (err) {
      setError(err.message || "Unable to upload resumes");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="recruiter-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="recruiter-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="recruiter-modal-header">
          <div>
            <h2>Add resume batch</h2>
            <p>Up to {MAX_FILES} files per batch — results append to this job's candidate table.</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close">x</button>
        </div>

        <div className="job-form">
          {error && <div className="job-form-error">{error}</div>}

          <label
            className={`pipeline-jd-dropzone${dragOver ? " drag-over" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          >
            <input type="file" multiple accept=".pdf,.docx" style={{ display: "none" }} onChange={(e) => handleFiles(e.target.files)} />
            {uploading ? "Uploading…" : `Drop up to ${MAX_FILES} resumes here, or click to browse`}
          </label>

          {batches.length > 0 && (
            <div className="job-form-block">
              <span className="job-form-block-label">Batch history</span>
              <div className="pipeline-batch-list">
                {batches.map((batch) => (
                  <div key={batch.batchId} className="pipeline-batch-row">
                    <span className="pipeline-batch-count">{batch.totalFiles} resumes</span>
                    <span className="rd-muted pipeline-batch-progress">
                      {batch.counts.completed} completed
                      {batch.counts.processing > 0 && ` · ${batch.counts.processing} processing`}
                      {batch.counts.pending > 0 && ` · ${batch.counts.pending} pending`}
                      {batch.counts.failed > 0 && ` · ${batch.counts.failed} failed`}
                    </span>
                    <span className={`rd-pill ${batch.status === "done" ? "active" : "inactive"}`}>
                      {batch.status === "done" ? "Done" : "Processing"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="recruiter-modal-actions">
          <button className="primary-button" type="button" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
